import { ShieldCheck, ShieldQuestion } from "lucide-react";

/**
 * Small verification badge — used on doctor cards, doctor profile
 * header, and patient slip. Only shows anything if the doctor has
 * submitted an NMC number (never leaks certificate URL / rejection notes
 * publicly — those are stripped by the backend).
 *
 * Props:
 *  - doctor: { verified: boolean, nmc_number?: string, verification_status?: string }
 *  - size: 'sm' (default) | 'md'
 *  - showPending: when true, shows a grey "pending" pill for submitted-but-unverified doctors
 */
export default function NmcBadge({ doctor, size = "sm", showPending = false, testid }) {
  if (!doctor) return null;
  const verified = !!doctor.verified;
  const hasSubmission = !!(doctor.nmc_number && String(doctor.nmc_number).trim());
  const peerCount = Number(doctor.trust_score || 0);
  const peerLine = peerCount > 0
    ? ` · Peer-referred by ${peerCount} doctor${peerCount === 1 ? "" : "s"}`
    : "";

  if (!verified && !hasSubmission) return null;

  const sz = size === "md"
    ? "text-xs px-2 py-1"
    : "text-[10px] px-1.5 py-0.5";
  const iconCls = size === "md" ? "w-3.5 h-3.5" : "w-3 h-3";

  if (verified) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold ${sz}`}
        data-testid={testid || "nmc-verified"}
        title={`NMC / MCI verified by UpchaarQ${peerLine}`}
      >
        <ShieldCheck className={iconCls} /> NMC Verified
        {peerCount > 0 && (
          <span className="text-emerald-800/70 font-normal ml-0.5">· {peerCount}★</span>
        )}
      </span>
    );
  }
  if (showPending && hasSubmission) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-md bg-slate-100 text-slate-500 border border-slate-200 font-medium ${sz}`}
        data-testid={testid || "nmc-pending"}
        title={`Verification is being reviewed${peerLine}`}
      >
        <ShieldQuestion className={iconCls} /> Pending
      </span>
    );
  }
  return null;
}
